// frontend/src/pages/ForgotPassword.js — Session 18
// Mot de passe oublié : email + téléphone → email de reset
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/logo_original.png';
import theme from '../theme';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ─── Formulaire "Mot de passe oublié" ─────────────────────────────────────────
const ForgotPasswordForm = () => {
  const [form,    setForm]    = useState({ email: '', telephone: '' });
  const [loading, setLoading] = useState(false);
  const [msg,     setMsg]     = useState('');
  const [error,   setError]   = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.telephone.trim()) {
      setError('Email et téléphone obligatoires.'); return;
    }
    setLoading(true); setError(''); setMsg('');
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, form);
      setMsg('✅ Si cette combinaison correspond à un compte, un email de réinitialisation a été envoyé. Vérifiez votre boîte mail (et les spams).');
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <img src={logo} alt="MKW Solutions" style={styles.logo} />
        </div>

        <h2 style={styles.title}>🔐 Mot de passe oublié</h2>
        <p style={styles.desc}>
          Entrez votre <strong>adresse email</strong> et votre <strong>numéro de téléphone</strong>
          enregistrés sur votre compte. Un lien de réinitialisation vous sera envoyé.
        </p>

        {error && <div style={styles.errorBox}>⚠️ {error}</div>}
        {msg   && <div style={styles.successBox}>{msg}</div>}

        {!msg && (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Adresse email</label>
              <input
                type="email" value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                required placeholder="votre@email.com"
                style={styles.input}
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>Numéro de téléphone</label>
              <input
                type="tel" value={form.telephone}
                onChange={e => setForm(f => ({ ...f, telephone: e.target.value }))}
                required placeholder="+32 4XX XX XX XX ou 04XX XX XX XX"
                style={styles.input}
              />
              <span style={{ fontSize: '12px', color: '#999', marginTop: '3px' }}>
                Doit correspondre au numéro enregistré sur votre compte.
              </span>
            </div>

            <button type="submit" disabled={loading} style={{
              ...styles.submitBtn,
              opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer',
            }}>
              {loading ? '⏳ Envoi en cours…' : '📧 Envoyer le lien de réinitialisation'}
            </button>
          </form>
        )}

        <div style={styles.backLink}>
          <Link to="/login" style={{ color: theme.primary, fontWeight: '600', textDecoration: 'none' }}>
            ← Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─── Formulaire "Nouveau mot de passe" (via token dans l'URL) ─────────────────
const ResetPasswordForm = () => {
  const [searchParams]       = useSearchParams();
  const token                = searchParams.get('token') || '';
  const [form, setForm]      = useState({ nouveau: '', confirm: '' });
  const [showN, setShowN]    = useState(false);
  const [showC, setShowC]    = useState(false);
  const [loading, setLoading] = useState(false);
  const [msg,    setMsg]      = useState('');
  const [error,  setError]    = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.nouveau.trim().length < 6) {
      setError('Le mot de passe doit faire au moins 6 caractères.'); return;
    }
    if (form.nouveau !== form.confirm) {
      setError('Les deux mots de passe ne correspondent pas.'); return;
    }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/auth/reset-password`, {
        token, nouveau_mot_de_passe: form.nouveau,
      });
      setMsg('✅ Mot de passe modifié avec succès ! Vous pouvez maintenant vous connecter.');
    } catch (err) {
      setError(err.response?.data?.message || 'Lien invalide ou expiré. Veuillez refaire une demande.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.errorBox}>❌ Lien invalide. Veuillez refaire une demande de réinitialisation.</div>
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <Link to="/forgot-password" style={{ color: theme.primary }}>← Refaire une demande</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <img src={logo} alt="MKW Solutions" style={styles.logo} />
        </div>

        <h2 style={styles.title}>🔑 Nouveau mot de passe</h2>
        <p style={styles.desc}>Choisissez un nouveau mot de passe sécurisé (min. 6 caractères).</p>

        {error && <div style={styles.errorBox}>⚠️ {error}</div>}
        {msg   && (
          <div style={styles.successBox}>
            {msg}
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <Link to="/login" style={{ color: theme.primary, fontWeight: '600' }}>
                → Se connecter
              </Link>
            </div>
          </div>
        )}

        {!msg && (
          <form onSubmit={handleSubmit} style={styles.form}>
            {[
              { key: 'nouveau',  label: 'Nouveau mot de passe',  show: showN, toggle: () => setShowN(v=>!v), ph: 'Min. 6 caractères' },
              { key: 'confirm',  label: 'Confirmer le mot de passe', show: showC, toggle: () => setShowC(v=>!v), ph: 'Répétez le mot de passe' },
            ].map(f => (
              <div key={f.key} style={styles.fieldGroup}>
                <label style={styles.label}>{f.label}</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={f.show ? 'text' : 'password'}
                    value={form[f.key]}
                    onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                    required placeholder={f.ph}
                    style={{ ...styles.input, paddingRight: '44px', width: '100%', boxSizing: 'border-box' }}
                  />
                  <button type="button" onClick={f.toggle} style={styles.eyeBtn}>{f.show ? '🙈' : '👁'}</button>
                </div>
              </div>
            ))}

            <button type="submit" disabled={loading} style={{
              ...styles.submitBtn,
              opacity: loading ? 0.7 : 1, cursor: loading ? 'not-allowed' : 'pointer',
            }}>
              {loading ? '⏳ Enregistrement…' : '💾 Enregistrer le nouveau mot de passe'}
            </button>
          </form>
        )}

        <div style={styles.backLink}>
          <Link to="/login" style={{ color: theme.primary, fontWeight: '600', textDecoration: 'none' }}>
            ← Retour à la connexion
          </Link>
        </div>
      </div>
    </div>
  );
};

// ─── Export conditionnel selon la route ───────────────────────────────────────
export const ForgotPassword = ForgotPasswordForm;
export const ResetPassword  = ResetPasswordForm;
export default ForgotPasswordForm;

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    background: theme.gradientPage || 'linear-gradient(135deg, #f0f4f8 0%, #dce8f0 100%)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
  },
  card: {
    background: 'white', borderRadius: '16px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
    padding: '40px 36px', width: '100%', maxWidth: '440px',
  },
  header: { textAlign: 'center', marginBottom: '24px' },
  logo: { width: '180px', height: 'auto', borderRadius: '8px' },
  title: { margin: '0 0 10px', color: theme.textPrimary, fontSize: '20px', fontWeight: '700' },
  desc: { color: '#666', fontSize: '14px', margin: '0 0 20px', lineHeight: '1.5' },
  errorBox: {
    background: '#fff5f5', color: '#e53e3e',
    border: '1px solid #fed7d7', borderRadius: '8px',
    padding: '12px 14px', marginBottom: '16px', fontSize: '13px',
  },
  successBox: {
    background: '#f0fff4', color: '#276749',
    border: '1px solid #c6f6d5', borderRadius: '8px',
    padding: '14px 16px', fontSize: '14px', lineHeight: '1.6',
  },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  label: { fontSize: '14px', fontWeight: '500', color: theme.textPrimary },
  input: {
    padding: '11px 14px', border: `1px solid ${theme.border}`,
    borderRadius: '8px', fontSize: '14px', outline: 'none',
    color: theme.textPrimary, display: 'block',
  },
  eyeBtn: {
    position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', padding: '2px',
  },
  submitBtn: {
    padding: '13px', background: theme.gradientPrimary,
    color: 'white', border: 'none', borderRadius: '8px',
    fontSize: '15px', fontWeight: '600', width: '100%',
  },
  backLink: { textAlign: 'center', marginTop: '20px', fontSize: '14px' },
};
